import React, { useEffect, useRef } from 'react';
import SectionHeader from './SectionHeader';

const steps = [
  {
    n: '1',
    title: 'Connect your repository',
    desc: 'Link your GitHub, GitLab, or Bitbucket account and select your project.',
  },
  {
    n: '2',
    title: 'Configure',
    desc: 'Set your environment variables and build settings. It just works out of the box.',
  },
  {
    n: '3',
    title: 'Deploy',
    desc: 'Push code to your main branch. We build, deploy, and scale automatically.',
  },
  {
    n: '4',
    title: 'Go live',
    desc: 'Your app is live with a global CDN, ready to share with your users.',
  },
];

const HowItWorks = () => {
  const sectionRef = useRef(null);
  const railRef = useRef(null);
  const stepRefs = useRef([]);
  const litRef = useRef(-1);

  useEffect(() => {
    const root = document.querySelector('.landing-page');
    const section = sectionRef.current;
    if (!root || !section) return undefined;

    const apply = (count) => {
      if (litRef.current === count) return;
      litRef.current = count;

      stepRefs.current.forEach((el, i) => {
        if (!el) return;
        const lit = i < count;
        const current = i === count - 1;
        el.classList.toggle('is-lit', lit);
        el.classList.toggle('is-current', current);
      });

      if (railRef.current) {
        const pct = count <= 1 ? 0 : ((count - 1) / 3) * 100;
        railRef.current.style.setProperty('--lp-rail-progress', `${pct}%`);
      }
    };

    const update = () => {
      const viewH = root.clientHeight || window.innerHeight;
      const sectionRect = section.getBoundingClientRect();
      const start = viewH * 0.78;
      const end = viewH * 0.3;
      const anchor = sectionRect.top + Math.min(sectionRect.height * 0.3, 180);
      const progress = Math.min(1, Math.max(0, (start - anchor) / (start - end)));
      const count = progress <= 0.03 ? 0 : Math.min(4, Math.ceil(progress * 4));
      apply(count);
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    };

    root.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();

    return () => {
      root.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <div className="lp-section lp-how" ref={sectionRef}>
      <div className="lp-container">
        <SectionHeader
          badge="How it works"
          title="From code to live in minutes"
          subtitle="A simple, powerful workflow to get your product online."
        />

        <div className="lp-steps-wrap">
          <div className="lp-steps-rail" aria-hidden="true" ref={railRef} />
          <ol className="lp-steps">
            {steps.map((step, i) => (
              <li
                key={step.n}
                className="lp-step"
                ref={(el) => { stepRefs.current[i] = el; }}
              >
                <span className="lp-step-num" aria-hidden="true">{step.n}</span>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
